<template>
  <div class="course-detail-page">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading course details...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <i class="fas fa-exclamation-circle"></i>
      <h2>Unable to Load Course</h2>
      <p>{{ error }}</p>
      <button @click="loadCourse" class="btn btn-primary">Try Again</button>
      <router-link to="/learning" class="btn btn-secondary">Back to Catalog</router-link>
    </div>

    <!-- Course Content -->
    <div v-else-if="course" class="course-content">
      <!-- Breadcrumb -->
      <nav class="breadcrumb">
        <router-link to="/learning">
          <i class="fas fa-book-open"></i>
          Course Catalog
        </router-link>
        <span class="separator">/</span>
        <span class="current">{{ course.title }}</span>
      </nav>

      <!-- Course Header -->
      <div class="course-header">
        <div class="course-header-content">
          <div class="course-meta">
            <span class="course-type" :class="`type-${course.course_type?.toLowerCase()}`">
              {{ formatCourseType(course.course_type) }}
            </span>
            <span class="difficulty-badge" :class="`difficulty-${course.difficulty_level?.toLowerCase()}`">
              {{ course.difficulty_level }}
            </span>
            <span v-if="course.category_name" class="category-badge">
              {{ course.category_name }}
            </span>
          </div>

          <h1 class="course-title">{{ course.title }}</h1>
          
          <!-- What You'll Learn (moved from Overview tab) -->
          <div v-if="course.learning_objectives && course.learning_objectives.length > 0" class="learning-objectives-header">
            <h3>What You'll Learn</h3>
            <ul>
              <li v-for="(objective, index) in course.learning_objectives" :key="index">
                <i class="fas fa-check-circle"></i>
                {{ objective }}
              </li>
            </ul>
          </div>

          <div class="course-stats">
            <div class="stat">
              <i class="fas fa-clock"></i>
              <span>{{ formatDuration(course.estimated_duration_hours) }}</span>
            </div>
            <div class="stat" v-if="instructorDisplayName">
              <i class="fas fa-user"></i>
              <span>{{ instructorDisplayName }}</span>
            </div>
            <div class="stat" v-if="course.average_rating">
              <i class="fas fa-star"></i>
              <span>{{ course.average_rating }} / 5.0 ({{ course.total_reviews }} reviews)</span>
            </div>
            <div class="stat">
              <i class="fas fa-users"></i>
              <span>{{ course.enrollment_count || 0 }} enrolled</span>
            </div>
          </div>

          <!-- Certification Journey Tracker - ALWAYS VISIBLE -->
          <div v-if="hasCertification" class="certification-journey">
            <div class="journey-header">
              <i class="fas fa-award"></i>
              <div class="journey-title">
                <strong>{{ course.is_enrolled ? 'Your Certification Progress' : 'Earn Your Certificate' }}</strong>
                <span v-if="course.credit_hours">{{ course.credit_hours }} CE Credits • RACE Approved</span>
              </div>
            </div>
            
            <div class="journey-steps">
              <!-- Step 1: Enrollment -->
              <div class="journey-step" :class="{ completed: course.is_enrolled, active: !course.is_enrolled }">
                <div class="step-icon">
                  <i :class="course.is_enrolled ? 'fas fa-check-circle' : 'far fa-circle'"></i>
                </div>
                <div class="step-content">
                  <span class="step-label">Step 1: Enroll</span>
                  <span class="step-status">{{ course.is_enrolled ? 'Enrolled' : 'Not enrolled' }}</span>
                </div>
              </div>

              <!-- Step 2: Watch Videos -->
              <div class="journey-step" :class="{ 
                completed: videosCompleted, 
                active: course.is_enrolled && !videosCompleted,
                locked: !course.is_enrolled 
              }">
                <div class="step-icon">
                  <i :class="videosCompleted ? 'fas fa-check-circle' : (course.is_enrolled ? 'fas fa-play-circle' : 'fas fa-lock')"></i>
                </div>
                <div class="step-content">
                  <span class="step-label">Step 2: Watch Videos</span>
                  <span class="step-status">
                    <template v-if="course.is_enrolled && course.user_progress">
                      {{ course.user_progress.videos_completed || 0 }} of {{ course.user_progress.videos_total || course.video_count || 0 }} completed
                    </template>
                    <template v-else>
                      {{ course.video_count || 0 }} videos to watch
                    </template>
                  </span>
                  <!-- Mini progress bar for enrolled users -->
                  <div v-if="course.is_enrolled && course.user_progress" class="step-progress">
                    <div class="progress-bar-mini">
                      <div class="progress-fill-mini" :style="{ width: `${course.user_progress.progress_percentage || 0}%` }"></div>
                    </div>
                    <span class="progress-percent">{{ Math.round(course.user_progress.progress_percentage || 0) }}%</span>
                  </div>
                </div>
              </div>

              <!-- Step 3: Pass Quiz (if quiz exists) -->
              <div v-if="hasQuiz" class="journey-step" :class="{ 
                completed: quizPassed, 
                active: course.is_enrolled && videosCompleted && !quizPassed,
                locked: !course.is_enrolled || !videosCompleted 
              }">
                <div class="step-icon">
                  <i :class="quizPassed ? 'fas fa-check-circle' : (course.is_enrolled && videosCompleted ? 'fas fa-clipboard-check' : 'fas fa-lock')"></i>
                </div>
                <div class="step-content">
                  <span class="step-label">Step 3: Pass Quiz</span>
                  <span class="step-status">
                    <template v-if="quizPassed">
                      Passed with {{ quizScore }}%
                    </template>
                    <template v-else-if="quizAttempted">
                      Best: {{ quizScore }}% ({{ course.quiz_passing_score || 70 }}% required)
                    </template>
                    <template v-else>
                      {{ course.quiz_questions?.length || 0 }} questions • {{ course.quiz_passing_score || 70 }}% to pass
                    </template>
                  </span>
                </div>
              </div>

              <!-- Step 4: Get Certificate -->
              <div class="journey-step" :class="{ 
                completed: isCompleted, 
                active: course.is_enrolled && (hasQuiz ? quizPassed : videosCompleted) && !isCompleted,
                locked: !course.is_enrolled || (hasQuiz ? !quizPassed : !videosCompleted)
              }">
                <div class="step-icon">
                  <i :class="isCompleted ? 'fas fa-certificate' : 'fas fa-lock'"></i>
                </div>
                <div class="step-content">
                  <span class="step-label">{{ hasQuiz ? 'Step 4' : 'Step 3' }}: Get Certificate</span>
                  <span class="step-status">{{ isCompleted ? 'Ready to download!' : 'Complete all steps above' }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="course-actions">
            <div v-if="!course.is_enrolled" class="purchase-section">
              <!-- Free course benefit banner -->
              <div v-if="!course.is_free && freeCourseAvailable" class="free-course-notice">
                <i class="fas fa-gift"></i>
                <div>
                  <strong>1 free course/year — yours to use!</strong>
                  <span>You haven't used your annual member benefit yet. This course is on us.</span>
                </div>
              </div>

              <button 
                @click="enrollInCourse" 
                class="btn btn-large"
                :class="freeCourseAvailable && !course.is_free ? 'btn-free' : 'btn-primary'"
                :disabled="enrolling"
              >
                <i :class="course.is_free || freeCourseAvailable ? 'fas fa-gift' : 'fas fa-shopping-cart'"></i>
                <template v-if="enrolling">Processing...</template>
                <template v-else-if="course.is_free">Enroll in Course</template>
                <template v-else-if="freeCourseAvailable">Enroll for Free</template>
                <template v-else-if="isVerifiedMember">
                  Purchase for ${{ memberPrice }}
                </template>
                <template v-else>
                  Purchase for ${{ course.price }}
                </template>
              </button>
              <span v-if="!course.is_free && !freeCourseAvailable" class="member-discount-note" :class="{ 'verified': isVerifiedMember }">
                <i :class="isVerifiedMember ? 'fas fa-check-circle' : 'fas fa-star'"></i>
                <template v-if="isVerifiedMember">
                  <span class="original-price">${{ course.price }}</span>
                  {{ memberDiscountPercentage }}% Member Discount Applied
                </template>
                <template v-else>
                  Members get 40% off + 1 free course/year
                </template>
              </span>
            </div>
            <div v-else class="enrolled-actions">
              <!-- Show certificate button if completed -->
              <div v-if="isCompleted" class="completion-section">
                <div class="completion-badge">
                  <i class="fas fa-check-circle"></i>
                  <span>Course Completed!</span>
                </div>
                <div class="completion-actions">
                  <button 
                    @click="downloadCertificate" 
                    class="btn btn-certificate btn-large"
                    :disabled="downloadingCert"
                  >
                    <i class="fas fa-download"></i>
                    {{ downloadingCert ? 'Generating...' : 'Download PDF' }}
                  </button>
                  <button 
                    @click="continueLearning" 
                    class="btn btn-secondary btn-large"
                  >
                    <i class="fas fa-play"></i>
                    Rewatch Videos
                  </button>
                </div>
                <p v-if="course.credit_hours" class="ce-credits-note">
                  <i class="fas fa-award"></i>
                  {{ course.credit_hours }} CE Credits Earned
                </p>
              </div>

              <!-- Take Quiz button (if quiz required, videos completed at 95%+, and quiz not passed) -->
              <router-link
                v-else-if="course.quiz_questions && course.quiz_questions.length > 0 && !quizPassed && videosCompleted"
                :to="`/learning/courses/${course.id}/quiz`"
                class="btn btn-primary btn-large"
              >
                <i class="fas fa-clipboard-check"></i>
                {{ quizAttempted ? 'Retake Quiz' : 'Take Quiz' }}
              </router-link>
              
              <!-- Continue Learning button (when not completed - show when no quiz OR quiz not yet unlocked) -->
              <button 
                v-else-if="!isCompleted"
                @click="continueLearning" 
                class="btn btn-success btn-large"
              >
                <i class="fas fa-play"></i>
                Continue Learning
              </button>
              
              <button @click="toggleWishlist" class="btn btn-secondary">
                <i :class="isInWishlist ? 'fas fa-heart' : 'far fa-heart'"></i>
              </button>
            </div>
            
            <!-- Quiz locked info notice (shown below action buttons when quiz exists but videos not complete) -->
            <div 
              v-if="course.quiz_questions && course.quiz_questions.length > 0 && !quizPassed && !videosCompleted && course.is_enrolled"
              class="quiz-locked-notice"
            >
              <i class="fas fa-lock"></i>
              <div class="quiz-locked-text">
                <span class="quiz-locked-title">Quiz Locked</span>
                <span class="quiz-locked-desc">Complete watching all videos ({{ Math.round(course.user_progress?.progress_percentage || 0) }}% / 95% required) to unlock the quiz</span>
              </div>
            </div>
          </div>
        </div>

        <div class="course-thumbnail" v-if="course.thumbnail">
          <img :src="getThumbnailUrl(course.thumbnail)" :alt="course.title" />
        </div>
      </div>

      <!-- Course Content Tabs -->
      <div class="course-tabs">
        <div class="tab-buttons">
          <button 
            @click="activeTab = 'overview'" 
            :class="{ active: activeTab === 'overview' }"
            class="tab-button"
          >
            <i class="fas fa-info-circle"></i>
            Overview
          </button>
          <button 
            @click="activeTab = 'curriculum'" 
            :class="{ active: activeTab === 'curriculum' }"
            class="tab-button"
          >
            <i class="fas fa-list-ul"></i>
            Course Content
            <span v-if="course.video_count" class="tab-badge">{{ course.video_count }}</span>
          </button>
          <button 
            @click="activeTab = 'presenter'" 
            :class="{ active: activeTab === 'presenter' }"
            class="tab-button"
          >
            <i class="fas fa-user-tie"></i>
            Presenter
          </button>
          <button 
            @click="activeTab = 'reviews'" 
            :class="{ active: activeTab === 'reviews' }"
            class="tab-button"
          >
            <i class="fas fa-star"></i>
            Reviews
          </button>
          <button 
            @click="activeTab = 'research'; loadResearch()" 
            :class="{ active: activeTab === 'research' }"
            class="tab-button"
            v-if="course.is_enrolled"
          >
            <i class="fas fa-book-medical"></i>
            Research
            <span v-if="researchCount > 0" class="tab-badge research-badge">{{ researchCount }}</span>
          </button>
        </div>

        <!-- Tab Content -->
        <div class="tab-content">
          <!-- Overview Tab -->
          <div v-show="activeTab === 'overview'" class="tab-pane">
            <div class="overview-section">
              <h2>About This Course</h2>
              <div class="course-description" v-html="course.description"></div>

              <div v-if="course.prerequisites" class="prerequisites">
                <h3>Prerequisites</h3>
                <p>{{ course.prerequisites }}</p>
              </div>

              <div v-if="course.target_audience" class="target-audience">
                <h3>Who This Course Is For</h3>
                <p>{{ course.target_audience }}</p>
              </div>
            </div>
          </div>

          <!-- Course Content / Curriculum Tab -->
          <div v-show="activeTab === 'curriculum'" class="tab-pane">
            <div class="curriculum-section">
              <div class="curriculum-header">
                <h2>
                  <i class="fas fa-play-circle"></i>
                  Course Content
                </h2>
                <div class="curriculum-stats">
                  <span>{{ videos.length }} videos</span>
                  <span v-if="totalVideoDuration">• {{ formatTotalDuration(totalVideoDuration) }}</span>
                  <span v-if="course.is_enrolled && course.user_progress">
                    • {{ course.user_progress.videos_completed || 0 }}/{{ course.user_progress.videos_total || videos.length }} completed
                  </span>
                </div>
              </div>

              <!-- Video List -->
              <div v-if="loadingVideos" class="loading-container">
                <div class="spinner"></div>
                <p>Loading course content...</p>
              </div>

              <div v-else-if="videos.length > 0" class="video-list">
                <div 
                  v-for="(video, index) in videos" 
                  :key="video.id" 
                  class="video-item"
                  :class="{ 
                    completed: getVideoProgress(video.id)?.is_completed,
                    'in-progress': getVideoProgress(video.id)?.completion_percentage > 0 && !getVideoProgress(video.id)?.is_completed,
                    locked: !course.is_enrolled
                  }"
                  @click="course.is_enrolled ? openVideo(video) : null"
                >
                  <div class="video-index">
                    <template v-if="getVideoProgress(video.id)?.is_completed">
                      <i class="fas fa-check"></i>
                    </template>
                    <template v-else>
                      {{ index + 1 }}
                    </template>
                  </div>
                  
                  <div class="video-info">
                    <h4>{{ video.title }}</h4>
                    <p v-if="video.description" class="video-description">{{ video.description }}</p>
                    <div class="video-meta">
                      <span class="video-duration">
                        <i class="fas fa-clock"></i>
                        {{ formatVideoTime(video.duration_seconds) }}
                      </span>
                      <span v-if="getVideoProgress(video.id)?.is_completed" class="completed-badge">
                        <i class="fas fa-check-circle"></i>
                        Completed
                      </span>
                      <span v-else-if="getVideoProgress(video.id)?.completion_percentage > 0" class="progress-badge">
                        {{ Math.round(getVideoProgress(video.id).completion_percentage) }}% watched
                      </span>
                    </div>
                    <!-- Video progress bar -->
                    <div v-if="course.is_enrolled && getVideoProgress(video.id)?.completion_percentage > 0" class="video-progress-bar">
                      <div class="progress-fill" :style="{ width: `${getVideoProgress(video.id).completion_percentage}%` }"></div>
                    </div>
                  </div>

                  <div class="video-action">
                    <i v-if="!course.is_enrolled" class="fas fa-lock"></i>
                    <i v-else-if="getVideoProgress(video.id)?.is_completed" class="fas fa-check-circle completed-icon"></i>
                    <i v-else class="fas fa-play-circle"></i>
                  </div>
                </div>
              </div>

              <div v-else class="empty-state">
                <i class="fas fa-video"></i>
                <p>Course content is being prepared</p>
              </div>
            </div>
          </div>

          <!-- About Presenter Tab -->
          <div v-show="activeTab === 'presenter'" class="tab-pane">
            <div class="presenter-section">
              <h2>
                <i class="fas fa-user-tie"></i>
                About the Presenter
              </h2>
              
              <!-- Phase 1b — render structured instructors when present, fall back to legacy text -->
              <div v-if="instructorList.length" class="instructor-info">
                <div
                  v-for="(ins, idx) in instructorList"
                  :key="ins.user_id"
                  class="instructor-block"
                  :class="{ 'is-co-presenter': idx > 0 }"
                >
                  <div class="instructor-header">
                    <div class="instructor-avatar">
                      <img
                        v-if="ins.headshot_url"
                        :src="ins.headshot_url"
                        :alt="ins.full_name"
                      />
                      <i v-else class="fas fa-user-circle"></i>
                    </div>
                    <div class="instructor-details">
                      <h3>
                        <a v-if="ins.public_slug" :href="`/speakers/${ins.public_slug}`">
                          {{ ins.full_name }}
                        </a>
                        <template v-else>{{ ins.full_name }}</template>
                        <small v-if="idx > 0" class="instructor-role-badge">
                          ({{ ins.role === 'CO' ? 'Co-Presenter' : ins.role === 'GUEST' ? 'Guest' : '' }})
                        </small>
                      </h3>
                      <p v-if="ins.credentials" class="credentials">{{ ins.credentials }}</p>
                    </div>
                  </div>
                  <div v-if="ins.bio" class="instructor-bio">
                    <h4 v-if="idx === 0">Biography</h4>
                    <div class="bio-content">{{ ins.bio }}</div>
                  </div>
                </div>
              </div>

              <div v-else-if="course.instructor_name || course.instructor_bio || course.instructor_credentials" class="instructor-info">
                <div class="instructor-header">
                  <div class="instructor-avatar">
                    <i class="fas fa-user-circle"></i>
                  </div>
                  <div class="instructor-details">
                    <h3>{{ course.instructor_name || 'Instructor' }}</h3>
                    <p v-if="course.instructor_credentials" class="credentials">
                      {{ course.instructor_credentials }}
                    </p>
                  </div>
                </div>
                <div v-if="course.instructor_bio" class="instructor-bio">
                  <h4>Biography</h4>
                  <div class="bio-content">{{ course.instructor_bio }}</div>
                </div>
              </div>

              <div v-else class="empty-state">
                <i class="fas fa-user-tie"></i>
                <p>Instructor information not available</p>
              </div>
              
              <!-- Course Features (moved from header) -->
              <div v-if="course.short_description" class="course-features">
                <h3>Course Features</h3>
                <div class="features-text" v-html="course.short_description"></div>
              </div>

              <!-- Certification Information -->
              <div v-if="course.certification_type" class="certification-info">
                <h4>
                  <i class="fas fa-certificate"></i>
                  Certification
                </h4>
                <p>{{ course.certification_type }}</p>
              </div>
            </div>
          </div>

          <!-- Reviews Tab -->
          <div v-show="activeTab === 'reviews'" class="tab-pane">
            <div class="reviews-section">
              <h2>Student Reviews</h2>
              
              <div v-if="loadingReviews" class="loading-container">
                <div class="spinner"></div>
                <p>Loading reviews...</p>
              </div>

              <div v-else-if="reviews.length > 0" class="reviews-list">
                <div v-for="review in reviews" :key="review.id" class="review-item">
                  <div class="review-header">
                    <div class="reviewer-info">
                      <div class="reviewer-avatar">
                        <i class="fas fa-user-circle"></i>
                      </div>
                      <div>
                        <h4>{{ review.user_name }}</h4>
                        <div class="review-rating">
                          <i v-for="n in 5" :key="n" 
                             :class="n <= review.rating ? 'fas fa-star' : 'far fa-star'"
                          ></i>
                        </div>
                      </div>
                    </div>
                    <span class="review-date">{{ formatDate(review.created_at) }}</span>
                  </div>
                  <p class="review-text">{{ review.review_text }}</p>
                </div>
              </div>

              <div v-else class="empty-state">
                <i class="fas fa-star"></i>
                <p>No reviews yet. Be the first to review this course!</p>
              </div>
            </div>
          </div>

          <!-- Research Tab -->
          <div v-show="activeTab === 'research'" class="tab-pane">
            <div class="research-section">
              <!-- Unified Search Section -->
              <div class="unified-search-section">
                <div class="search-header-row">
                  <div class="search-title">
                    <h2>
                      <i class="fas fa-search"></i>
                      Search Courses & Research
                    </h2>
                    <p class="search-subtitle">Find courses and PubMed articles on any topic</p>
                  </div>
                </div>
                
                <div class="search-bar-wrapper">
                  <div class="search-input-container">
                    <i class="fas fa-search search-icon"></i>
                    <input
                      v-model="unifiedSearchQuery"
                      type="text"
                      placeholder="Search courses, PubMed, and research databases..."
                      class="unified-search-input"
                      @input="debouncedUnifiedSearch"
                      @keyup.enter="performUnifiedSearch"
                    />
                    <button 
                      v-if="unifiedSearchQuery" 
                      @click="clearUnifiedSearch"
                      class="clear-search-btn"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                  <button 
                    @click="performUnifiedSearch"
                    class="btn btn-primary search-submit-btn"
                    :disabled="!unifiedSearchQuery.trim() || searchingUnified"
                  >
                    <i :class="searchingUnified ? 'fas fa-spinner fa-spin' : 'fas fa-search'"></i>
                    Search
                  </button>
                </div>

                <!-- Search Results Tabs -->
                <div v-if="hasSearchResults" class="search-results-tabs">
                  <button 
                    @click="searchResultsTab = 'courses'" 
                    :class="['tab-btn', { active: searchResultsTab === 'courses' }]"
                  >
                    <i class="fas fa-graduation-cap"></i>
                    Courses ({{ unifiedCourseResults.length }})
                  </button>
                  <button 
                    @click="searchResultsTab = 'articles'" 
                    :class="['tab-btn', { active: searchResultsTab === 'articles' }]"
                  >
                    <i class="fas fa-file-medical-alt"></i>
                    Research Articles ({{ unifiedArticleResults.length }})
                  </button>
                  <button @click="clearUnifiedSearch" class="btn btn-sm btn-secondary clear-btn">
                    <i class="fas fa-times"></i>
                    Clear
                  </button>
                </div>

                <!-- Loading State -->
                <div v-if="searchingUnified" class="search-loading">
                  <div class="spinner"></div>
                  <p>Searching courses and PubMed...</p>
                </div>

                <!-- Courses Results -->
                <div v-else-if="hasSearchResults && searchResultsTab === 'courses'" class="search-results-content">
                  <div v-if="unifiedCourseResults.length > 0" class="course-results-grid">
                    <div 
                      v-for="searchCourse in unifiedCourseResults.slice(0, 6)" 
                      :key="searchCourse.id"
                      class="course-result-card"
                    >
                      <div class="course-result-thumbnail" v-if="searchCourse.thumbnail">
                        <img :src="getThumbnailUrl(searchCourse.thumbnail)" :alt="searchCourse.title" />
                      </div>
                      <div class="course-result-content">
                        <h4 class="course-result-title">{{ searchCourse.title }}</h4>
                        <p class="course-result-desc">{{ truncateText(searchCourse.short_description, 100) }}</p>
                        <div class="course-result-meta">
                          <span v-if="courseInstructorLabel(searchCourse)">
                            <i class="fas fa-user"></i>
                            {{ courseInstructorLabel(searchCourse) }}
                          </span>
                          <span v-if="searchCourse.credit_hours">
                            <i class="fas fa-award"></i>
                            {{ searchCourse.credit_hours }} CE
                          </span>
                        </div>
                        <router-link 
                          :to="`/learning/courses/${searchCourse.id}`"
                          class="btn btn-sm btn-primary course-result-btn"
                        >
                          <i class="fas fa-eye"></i>
                          View Course
                        </router-link>
                      </div>
                    </div>
                  </div>
                  <div v-else class="no-results">
                    <i class="fas fa-graduation-cap"></i>
                    <p>No courses found for "{{ unifiedSearchQuery }}"</p>
                  </div>
                </div>

                <!-- Research Articles Results -->
                <div v-else-if="hasSearchResults && searchResultsTab === 'articles'" class="search-results-content">
                  <div v-if="unifiedArticleResults.length > 0" class="article-results-list">
                    <div 
                      v-for="article in unifiedArticleResults" 
                      :key="article.pmid"
                      class="article-result-card"
                    >
                      <div class="article-header">
                        <span class="article-source">
                          <i class="fas fa-database"></i>
                          PubMed
                        </span>
                        <button 
                          @click="saveArticleToLibrary(article)"
                          class="btn btn-sm"
                          :class="article.is_saved ? 'btn-success' : 'btn-outline'"
                          :disabled="savingArticle === article.pmid"
                        >
                          <i :class="savingArticle === article.pmid ? 'fas fa-spinner fa-spin' : (article.is_saved ? 'fas fa-check' : 'fas fa-bookmark')"></i>
                          {{ article.is_saved ? 'Saved' : 'Save to Library' }}
                        </button>
                      </div>
                      <h4 class="article-title">
                        <a :href="article.url" target="_blank" rel="noopener">
                          {{ article.title }}
                        </a>
                      </h4>
                      <div class="article-meta">
                        <span v-if="article.authors?.length" class="authors">
                          <i class="fas fa-users"></i>
                          {{ formatAuthors(article.authors) }}
                        </span>
                        <span v-if="article.journal" class="journal">
                          <i class="fas fa-book"></i>
                          {{ article.journal }}
                        </span>
                        <span v-if="article.pub_date" class="date">
                          <i class="fas fa-calendar"></i>
                          {{ article.pub_date }}
                        </span>
                      </div>
                      <p v-if="article.abstract" class="article-abstract">
                        {{ truncateText(article.abstract, 250) }}
                      </p>
                      <div class="article-actions">
                        <a :href="article.url" target="_blank" rel="noopener" class="btn btn-sm btn-primary">
                          <i class="fas fa-external-link-alt"></i>
                          View on PubMed
                        </a>
                        <a 
                          v-if="article.doi" 
                          :href="`https://doi.org/${article.doi}`" 
                          target="_blank" 
                          rel="noopener" 
                          class="btn btn-sm btn-secondary"
                        >
                          <i class="fas fa-link"></i>
                          DOI
                        </a>
                      </div>
                    </div>
                  </div>
                  <div v-else class="no-results">
                    <i class="fas fa-file-medical-alt"></i>
                    <p>No research articles found for "{{ unifiedSearchQuery }}"</p>
                    <p class="no-results-hint">Try different keywords or check if you have a database connection configured.</p>
                  </div>
                </div>

                <!-- Initial/Empty State -->
                <div v-else-if="!searchingUnified && !hasSearchResults && unifiedSearched" class="no-results">
                  <i class="fas fa-search"></i>
                  <p>No results found for "{{ unifiedSearchQuery }}"</p>
                </div>
              </div>

              <!-- Divider -->
              <div class="section-divider"></div>

              <!-- My Saved Research Section -->
              <div class="research-header">
                <div class="research-title">
                  <h2>
                    <i class="fas fa-book-medical"></i>
                    My Research Library
                  </h2>
                  <p class="research-subtitle">Articles and resources you've saved for this course</p>
                </div>
                <router-link 
                  :to="`/learning/courses/${course.id}/video/${videos[0]?.id || ''}`" 
                  class="btn btn-secondary btn-sm"
                  v-if="videos.length > 0"
                >
                  <i class="fas fa-play"></i>
                  Continue Learning
                </router-link>
              </div>

              <div v-if="loadingResearch" class="loading-container">
                <div class="spinner"></div>
                <p>Loading research library...</p>
              </div>

              <div v-else-if="savedResources.length > 0" class="research-grid">
                <div 
                  v-for="resource in savedResources" 
                  :key="resource.id"
                  class="research-card"
                >
                  <div class="research-card-header">
                    <span class="resource-type" :class="resource.resource_type?.toLowerCase()">
                      {{ formatResourceType(resource.resource_type) }}
                    </span>
                    <button 
                      @click="toggleFavorite(resource)"
                      class="btn-icon"
                      :class="{ 'is-favorite': resource.is_favorite }"
                    >
                      <i :class="resource.is_favorite ? 'fas fa-star' : 'far fa-star'"></i>
                    </button>
                  </div>
                  
                  <h3 class="resource-title">
                    <a :href="resource.url" target="_blank" rel="noopener">
                      {{ resource.title }}
                    </a>
                  </h3>
                  
                  <div class="resource-meta">
                    <span v-if="resource.authors?.length" class="authors">
                      <i class="fas fa-users"></i>
                      {{ formatAuthors(resource.authors) }}
                    </span>
                    <span v-if="resource.journal" class="journal">
                      <i class="fas fa-book"></i>
                      {{ resource.journal }}
                    </span>
                    <span v-if="resource.publication_date" class="date">
                      <i class="fas fa-calendar"></i>
                      {{ resource.publication_date }}
                    </span>
                  </div>

                  <p v-if="resource.abstract" class="resource-abstract">
                    {{ truncateText(resource.abstract, 200) }}
                  </p>

                  <div v-if="resource.notes" class="resource-notes">
                    <i class="fas fa-sticky-note"></i>
                    <span>{{ resource.notes }}</span>
                  </div>

                  <div class="resource-actions">
                    <a :href="resource.url" target="_blank" rel="noopener" class="btn btn-sm btn-primary">
                      <i class="fas fa-external-link-alt"></i>
                      View Article
                    </a>
                    <button @click="removeFromCourse(resource)" class="btn btn-sm btn-secondary">
                      <i class="fas fa-times"></i>
                      Remove
                    </button>
                  </div>
                </div>
              </div>

              <div v-else class="empty-state research-empty">
                <i class="fas fa-book-medical"></i>
                <h3>No Research Saved Yet</h3>
                <p>As you watch videos, you can search for and save relevant research articles to build your reference library for this course.</p>
                <router-link 
                  :to="`/learning/courses/${course.id}/video/${videos[0]?.id || ''}`" 
                  class="btn btn-primary"
                  v-if="videos.length > 0"
                >
                  <i class="fas fa-play"></i>
                  Start Learning & Research
                </router-link>
              </div>

              <!-- Organization Resources Section -->
              <div v-if="orgResources.length > 0" class="org-resources-section">
                <h3>
                  <i class="fas fa-users"></i>
                  Resources From Your Team
                </h3>
                <p class="section-subtitle">Research saved by others in your organization</p>
                
                <div class="research-grid compact">
                  <div 
                    v-for="resource in orgResources" 
                    :key="'org-' + resource.id"
                    class="research-card compact"
                  >
                    <h4 class="resource-title">
                      <a :href="resource.url" target="_blank" rel="noopener">
                        {{ resource.title }}
                      </a>
                    </h4>
                    <div class="resource-meta">
                      <span v-if="resource.journal">{{ resource.journal }}</span>
                      <span v-if="resource.publication_date">{{ resource.publication_date }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Course Checkout Modal -->
    <CourseCheckout
      v-if="showCheckoutModal && course"
      :course="course"
      @close="showCheckoutModal = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, onBeforeRouteUpdate } from 'vue-router'
import { lmsAPI } from '@/services/lmsAPI.js'
import api from '@/services/api'
import auth from '@/services/auth'
import { useToast } from 'vue-toastification'
import CourseCheckout from '@/components/lms/payment/CourseCheckout.vue'

export default {
  name: 'CourseDetail',
  components: {
    CourseCheckout,
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  setup(props) {
    const router = useRouter()
    const toast = useToast()

    const course = ref(null)
    const videos = ref([])
    const reviews = ref([])
    const savedResources = ref([])
    const orgResources = ref([])
    const loading = ref(true)
    const loadingVideos = ref(false)
    const loadingReviews = ref(false)
    const loadingResearch = ref(false)
    
    // Unified search state (courses + research articles)
    const unifiedSearchQuery = ref('')
    const unifiedCourseResults = ref([])
    const unifiedArticleResults = ref([])
    const searchingUnified = ref(false)
    const unifiedSearched = ref(false)
    const searchResultsTab = ref('courses')
    const savingArticle = ref(null)
    
    // Computed for checking if we have results
    const hasSearchResults = computed(() => {
      return unifiedSearched.value && (unifiedCourseResults.value.length > 0 || unifiedArticleResults.value.length > 0)
    })
    const error = ref(null)
    const enrolling = ref(false)
    const activeTab = ref('overview')
    const isInWishlist = ref(false)
    const showCheckoutModal = ref(false)
    const downloadingCert = ref(false)

    // Research count computed from saved resources
    const researchCount = computed(() => savedResources.value.length)

    // Member pricing
    const isVerifiedMember = computed(() => auth.isVerifiedMember())
    const memberDiscountPercentage = computed(() => auth.getMemberDiscountPercentage())
    const freeCourseAvailable = computed(() => auth.hasAnnualFreeCourseAvailable())

    // Phase 1b — structured Course.instructors[]; sorted lead-first.
    const instructorList = computed(() => {
      const raw = course.value?.instructors
      if (!Array.isArray(raw) || raw.length === 0) return []
      return [...raw].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
    })
    // Display name for hero/quick-stat areas: prefer structured lead instructor,
    // fall back to legacy instructor_name when the M2M is empty.
    const instructorDisplayName = computed(() => {
      const lead = instructorList.value[0]
      if (lead) return lead.user?.full_name || lead.full_name || lead.name || ''
      return course.value?.instructor_name || ''
    })
    // Same logic but works for arbitrary "course-like" objects (search hits).
    const courseInstructorLabel = (c) => {
      if (!c) return ''
      const list = Array.isArray(c.instructors) ? c.instructors : []
      const lead = [...list].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))[0]
      if (lead) return lead.user?.full_name || lead.full_name || lead.name || ''
      return c.instructor_name || ''
    }
    const memberPrice = computed(() => {
      if (!course.value) return '0.00'
      const pricing = auth.calculateMemberPrice(course.value.price)
      return pricing.finalPrice.toFixed(2)
    })

    const loadCourse = async () => {
      loading.value = true
      error.value = null

      try {
        const response = await lmsAPI.getCourse(props.id)
        course.value = response.data
      } catch (err) {
        console.error('Error loading course:', err)
        error.value = err.response?.data?.detail || 'Failed to load course details'
        toast.error(error.value)
      } finally {
        loading.value = false
      }
    }

    const loadVideos = async () => {
      loadingVideos.value = true
      try {
        const response = await lmsAPI.getVideos({ course: props.id })
        videos.value = response.data.results || response.data
      } catch (err) {
        console.error('Error loading videos:', err)
        toast.error('Failed to load course content')
      } finally {
        loadingVideos.value = false
      }
    }

    const loadReviews = async () => {
      loadingReviews.value = true
      try {
        const response = await lmsAPI.getCourseReviews(props.id)
        reviews.value = response.data.results || response.data
      } catch (err) {
        console.error('Error loading reviews:', err)
      } finally {
        loadingReviews.value = false
      }
    }

    const loadResearch = async () => {
      if (!course.value?.is_enrolled) return
      
      loadingResearch.value = true
      try {
        const response = await lmsAPI.getResourcesForCourse(props.id, true)
        savedResources.value = response.data.my_resources || []
        orgResources.value = response.data.org_resources || []
      } catch (err) {
        console.error('Error loading research:', err)
        // Don't show error toast - this is non-critical
      } finally {
        loadingResearch.value = false
      }
    }

    const toggleFavorite = async (resource) => {
      try {
        await lmsAPI.toggleResourceFavorite(resource.id)
        resource.is_favorite = !resource.is_favorite
        toast.success(resource.is_favorite ? 'Added to favorites' : 'Removed from favorites')
      } catch (err) {
        console.error('Error toggling favorite:', err)
        toast.error('Failed to update favorite status')
      }
    }

    const removeFromCourse = async (resource) => {
      try {
        await lmsAPI.untagResourceFromCourse(resource.id, props.id)
        savedResources.value = savedResources.value.filter(r => r.id !== resource.id)
        toast.success('Resource removed from this course')
      } catch (err) {
        console.error('Error removing resource:', err)
        toast.error('Failed to remove resource')
      }
    }

    const formatResourceType = (type) => {
      const types = {
        'PUBMED': 'PubMed',
        'EXTERNAL': 'Article',
        'COURSE_MATERIAL': 'Course Material'
      }
      return types[type] || type
    }

    const formatAuthors = (authors) => {
      if (!authors || authors.length === 0) return ''
      if (authors.length === 1) return authors[0]
      if (authors.length === 2) return authors.join(' & ')
      return `${authors[0]} et al.`
    }

    const truncateText = (text, maxLength) => {
      if (!text || text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    }

    // Unified search functions (courses + PubMed)
    const performUnifiedSearch = async () => {
      if (!unifiedSearchQuery.value.trim()) return
      
      searchingUnified.value = true
      unifiedSearched.value = true
      
      try {
        const response = await lmsAPI.unifiedSearch(unifiedSearchQuery.value, 'all', 20)
        
        if (response.data.success) {
          // Get courses (exclude current course)
          unifiedCourseResults.value = (response.data.courses || []).filter(
            c => c.id !== parseInt(props.id)
          )
          
          // Get research articles
          unifiedArticleResults.value = response.data.research_articles || []
          
          // Auto-switch to articles tab if we have more articles than courses
          // or if we only have articles
          if (unifiedArticleResults.value.length > unifiedCourseResults.value.length) {
            searchResultsTab.value = 'articles'
          } else if (unifiedCourseResults.value.length > 0) {
            searchResultsTab.value = 'courses'
          } else if (unifiedArticleResults.value.length > 0) {
            searchResultsTab.value = 'articles'
          }
        }
      } catch (err) {
        console.error('Error in unified search:', err)
        toast.error('Failed to search. Please try again.')
      } finally {
        searchingUnified.value = false
      }
    }

    const debouncedUnifiedSearch = (() => {
      let timeout = null
      return () => {
        if (timeout) clearTimeout(timeout)
        timeout = setTimeout(() => {
          if (unifiedSearchQuery.value.trim().length >= 2) {
            performUnifiedSearch()
          }
        }, 500)
      }
    })()

    const clearUnifiedSearch = () => {
      unifiedSearchQuery.value = ''
      unifiedCourseResults.value = []
      unifiedArticleResults.value = []
      unifiedSearched.value = false
      searchResultsTab.value = 'courses'
    }

    const saveArticleToLibrary = async (article) => {
      savingArticle.value = article.pmid
      
      try {
        await lmsAPI.saveResourceFromSearch({
          pmid: article.pmid,
          title: article.title,
          authors: article.authors || [],
          abstract: article.abstract || '',
          journal: article.journal || '',
          pub_date: article.pub_date || '',
          url: article.url,
          doi: article.doi || '',
          pub_type: article.pub_type || [],
          course_ids: [parseInt(props.id)]  // Tag to current course
        })
        
        article.is_saved = true
        toast.success('Article saved to your research library!')
        
        // Refresh saved resources
        loadResearch()
      } catch (err) {
        console.error('Error saving article:', err)
        if (err.response?.data?.error?.includes('already exists')) {
          article.is_saved = true
          toast.info('Article already in your library')
        } else {
          toast.error('Failed to save article')
        }
      } finally {
        savingArticle.value = null
      }
    }

    const enrollInCourse = async () => {
      // Check if course is paid
      if (course.value && !course.value.is_free && course.value.price > 0) {
        // Show checkout modal for paid courses
        showCheckoutModal.value = true
        return
      }

      // Free course - enroll directly
      enrolling.value = true
      try {
        await lmsAPI.enrollInCourse(props.id)
        course.value.is_enrolled = true
        toast.success('Successfully enrolled! Starting course...')
        
        // Redirect to first video after brief delay
        setTimeout(() => {
          continueLearning()
        }, 800)
        
      } catch (err) {
        console.error('Error enrolling:', err)
        const errorMsg = err.response?.data?.course?.[0] || 
                        err.response?.data?.error || 
                        'Failed to enroll in course'
        toast.error(errorMsg)
      } finally {
        enrolling.value = false
      }
    }

    const continueLearning = () => {
      if (videos.value.length > 0) {
        const firstIncomplete = videos.value.find(v => !v.is_completed) || videos.value[0]
        openVideo(firstIncomplete)
      } else {
        toast.info('Course content is not yet available')
      }
    }

    const openVideo = (video) => {
      router.push({
        name: 'CourseVideoPlayer',
        params: {
          courseId: props.id,
          videoId: video.id
        }
      })
    }


    const toggleWishlist = () => {
      isInWishlist.value = !isInWishlist.value
      toast.success(isInWishlist.value ? 'Added to wishlist' : 'Removed from wishlist')
    }

    // Check if course has a quiz requirement
    const hasQuizRequirement = computed(() => {
      return course.value?.quiz_questions && course.value.quiz_questions.length > 0
    })

    const quizPassed = computed(() => {
      return course.value?.user_progress?.completion_criteria?.quiz_passed === true
    })

    // SIMPLIFIED: Course is completed when:
    // - If course has quiz: quiz must be PASSED (regardless of video progress)
    // - If no quiz: all videos watched (>= 95% progress)
    const isCompleted = computed(() => {
      // If course has a quiz, completion is ONLY determined by quiz pass status
      if (hasQuizRequirement.value) {
        console.log('[CourseDetail] Quiz required. quiz_passed:', quizPassed.value)
        return quizPassed.value
      }
      
      // No quiz - completed when videos are done
      const videosProgress = course.value?.user_progress?.progress_percentage || 0
      console.log('[CourseDetail] No quiz. Video progress:', videosProgress)
      return videosProgress >= 95
    })

    const quizAttempted = computed(() => {
      return course.value?.user_progress?.completion_criteria?.quiz_attempt_count > 0
    })

    const quizScore = computed(() => {
      return course.value?.user_progress?.completion_criteria?.quiz_score || 0
    })

    // Check if course has certification available
    const hasCertification = computed(() => {
      return (course.value?.quiz_questions && course.value.quiz_questions.length > 0) ||
             course.value?.credit_hours ||
             course.value?.certification_type
    })

    // Alias for template compatibility
    const hasQuiz = hasQuizRequirement

    // Check if all videos are completed (>= 95% progress)
    const videosCompleted = computed(() => {
      if (!course.value?.is_enrolled) return false
      return (course.value?.user_progress?.progress_percentage || 0) >= 95
    })

    // Total video duration
    const totalVideoDuration = computed(() => {
      return videos.value.reduce((sum, v) => sum + (v.duration_seconds || 0), 0)
    })

    // Get video progress for a specific video
    const getVideoProgress = (videoId) => {
      if (!course.value?.user_progress?.video_progress) return null
      return course.value.user_progress.video_progress.find(vp => vp.video_id === videoId)
    }

    // Format total duration
    const formatTotalDuration = (seconds) => {
      if (!seconds) return ''
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      if (hours > 0) {
        return `${hours}h ${minutes}m total`
      }
      return `${minutes}m total`
    }

    const downloadCertificate = async () => {
      downloadingCert.value = true
      try {
        const response = await api.get(`/lms/courses/${props.id}/certificate/`, {
          responseType: 'blob'
        })
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        const filename = `Certificate_${course.value.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        
        toast.success('Certificate downloaded successfully!')
      } catch (error) {
        console.error('Certificate download error:', error)
        if (error.response?.status === 403) {
          toast.error('You must complete the course to download a certificate')
        } else {
          toast.error('Failed to download certificate. Please try again.')
        }
      } finally {
        downloadingCert.value = false
      }
    }

    const formatCourseType = (type) => {
      if (!type) return ''
      return type.replace('_', ' ')
    }

    const formatDuration = (hours) => {
      if (!hours) return 'Duration not specified'
      if (hours < 1) return `${Math.round(hours * 60)} minutes`
      return `${hours} hour${hours !== 1 ? 's' : ''}`
    }

    const formatVideoTime = (seconds) => {
      if (!seconds) return '0:00'
      return lmsAPI.formatDuration(seconds)
    }

    const formatDate = (dateString) => {
      return lmsAPI.formatDate(dateString)
    }

    const getThumbnailUrl = (thumbnail) => {
      if (thumbnail.startsWith('http')) return thumbnail
      return `${process.env.VUE_APP_API_URL || ''}/media/${thumbnail}`
    }

    // Function to refresh course data (reusable for mount and navigation)
    const refreshCourseData = async () => {
      await loadCourse()
      if (course.value) {
        loadVideos()
        loadReviews()
      }
    }

    // Track last refresh time to avoid excessive API calls
    const lastRefreshTime = ref(0)
    const REFRESH_COOLDOWN_MS = 5000  // 5 seconds minimum between refreshes

    // Handle visibility change to refresh when user returns to tab/page
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && course.value?.is_enrolled) {
        const now = Date.now()
        if (now - lastRefreshTime.value > REFRESH_COOLDOWN_MS) {
          console.log('📊 Page visible - refreshing progress data')
          lastRefreshTime.value = now
          await loadCourse()  // Refresh to get latest progress
        }
      }
    }

    // Handle window focus (user clicks back into browser window)
    const handleWindowFocus = async () => {
      if (course.value?.is_enrolled) {
        const now = Date.now()
        if (now - lastRefreshTime.value > REFRESH_COOLDOWN_MS) {
          console.log('📊 Window focused - refreshing progress data')
          lastRefreshTime.value = now
          await loadCourse()  // Refresh to get latest progress
        }
      }
    }

    onMounted(async () => {
      lastRefreshTime.value = Date.now()
      await refreshCourseData()

      const urlParams = new URLSearchParams(window.location.search)

      // Check for successful enrollment from payment
      if (urlParams.get('enrollment') === 'success') {
        toast.success('Payment successful! You are now enrolled in this course.')
        window.history.replaceState({}, '', window.location.pathname)
        await loadCourse()
      } else if (
        urlParams.get('checkout') === 'true'
        && course.value
        && !course.value.is_enrolled
        && !course.value.is_free
        && course.value.price > 0
      ) {
        showCheckoutModal.value = true
        window.history.replaceState({}, '', window.location.pathname)
      }

      // Listen for page visibility changes (user returns from video player)
      document.addEventListener('visibilitychange', handleVisibilityChange)
      // Also listen for window focus (catches navigation back)
      window.addEventListener('focus', handleWindowFocus)
    })

    onUnmounted(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleWindowFocus)
    })

    // Handle navigation to same route with different params (Vue Router)
    onBeforeRouteUpdate(async (to, from) => {
      if (to.params.id !== from.params.id) {
        console.log('📊 Course ID changed via route - reloading course data')
        await refreshCourseData()
      }
    })

    // Also watch for prop changes
    watch(() => props.id, async (newId, oldId) => {
      if (newId !== oldId) {
        console.log('📊 Course ID prop changed - reloading course data')
        await refreshCourseData()
      }
    })

    return {
      course,
      instructorList,
      instructorDisplayName,
      courseInstructorLabel,
      videos,
      reviews,
      savedResources,
      orgResources,
      loading,
      loadingVideos,
      loadingReviews,
      loadingResearch,
      error,
      enrolling,
      activeTab,
      isInWishlist,
      showCheckoutModal,
      downloadingCert,
      researchCount,
      isCompleted,
      quizPassed,
      quizAttempted,
      quizScore,
      hasCertification,
      hasQuiz,
      videosCompleted,
      totalVideoDuration,
      getVideoProgress,
      formatTotalDuration,
      loadCourse,
      loadResearch,
      enrollInCourse,
      continueLearning,
      openVideo,
      toggleWishlist,
      toggleFavorite,
      removeFromCourse,
      downloadCertificate,
      formatCourseType,
      formatDuration,
      formatVideoTime,
      formatDate,
      formatResourceType,
      formatAuthors,
      truncateText,
      getThumbnailUrl,
      // Member pricing
      isVerifiedMember,
      memberDiscountPercentage,
      freeCourseAvailable,
      memberPrice,
      // Unified search (courses + research)
      unifiedSearchQuery,
      unifiedCourseResults,
      unifiedArticleResults,
      searchingUnified,
      unifiedSearched,
      searchResultsTab,
      hasSearchResults,
      savingArticle,
      performUnifiedSearch,
      debouncedUnifiedSearch,
      clearUnifiedSearch,
      saveArticleToLibrary
    }
  }
}
</script>

<style scoped>
.course-detail-page {
  max-width: 1400px;
  margin: 0 auto;
  padding: var(--spacing-lg);
}

/* Loading & Error States */
.loading-container,
.error-container {
  text-align: center;
  padding: var(--spacing-xxl);
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: var(--color-primary);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto var(--spacing-md);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-container i {
  font-size: 3rem;
  color: var(--color-danger);
  margin-bottom: var(--spacing-md);
}

.error-container .btn {
  margin: var(--spacing-sm);
}

/* Breadcrumb */
.breadcrumb {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-lg);
  font-size: 0.9rem;
  color: var(--color-text-secondary);
}

.breadcrumb a {
  color: var(--color-primary);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.breadcrumb a:hover {
  text-decoration: underline;
}

.breadcrumb .separator {
  color: var(--color-text-secondary);
}

.breadcrumb .current {
  color: var(--color-text-primary);
  font-weight: 600;
}

/* Course Header */
.course-header {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--spacing-xl);
  margin-bottom: var(--spacing-xxl);
  padding: var(--spacing-xl);
  background: white;
  border-radius: var(--border-radius);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.course-meta {
  display: flex;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
  flex-wrap: wrap;
}

.course-type,
.difficulty-badge,
.category-badge {
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
}

.course-type {
  background: #e3f2fd;
  color: #1976d2;
}

.difficulty-badge {
  background: #f3e5f5;
  color: #7b1fa2;
}

.difficulty-beginner { background: #e8f5e9; color: #388e3c; }
.difficulty-intermediate { background: #fff3e0; color: #f57c00; }
.difficulty-advanced { background: #fce4ec; color: #c2185b; }

.category-badge {
  background: #f5f5f5;
  color: #616161;
}

.course-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-md) 0;
  line-height: 1.3;
}

.course-short-description {
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-lg);
  line-height: 1.6;
}

.course-stats {
  display: flex;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
  flex-wrap: wrap;
}

.stat {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  color: var(--color-text-secondary);
}

.stat i {
  color: var(--color-primary);
}

/* Certification Journey Tracker */
.certification-journey {
  margin: var(--spacing-lg) 0;
  padding: var(--spacing-lg);
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(16, 185, 129, 0.08) 100%);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 12px;
}

.journey-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
  padding-bottom: var(--spacing-md);
  border-bottom: 1px solid rgba(245, 158, 11, 0.2);
}

.journey-header i {
  font-size: 2rem;
  color: #d97706;
}

.journey-title {
  flex: 1;
}

.journey-title strong {
  display: block;
  font-size: 1.2rem;
  color: var(--color-text);
  margin-bottom: 4px;
}

.journey-title span {
  font-size: 0.9rem;
  color: #059669;
  font-weight: 600;
}

.journey-steps {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.journey-step {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
  padding: var(--spacing-sm);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.journey-step.completed {
  background: rgba(16, 185, 129, 0.1);
}

.journey-step.active {
  background: rgba(59, 130, 246, 0.1);
}

.journey-step.locked {
  opacity: 0.6;
}

.step-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.step-icon i {
  font-size: 1.5rem;
}

.journey-step.completed .step-icon i {
  color: #10b981;
}

.journey-step.active .step-icon i {
  color: #3b82f6;
}

.journey-step.locked .step-icon i {
  color: #9ca3af;
}

.step-content {
  flex: 1;
}

.step-label {
  display: block;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 2px;
}

.step-status {
  font-size: 0.85rem;
  color: var(--color-text-secondary);
}

.step-progress {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-xs);
}

.progress-bar-mini {
  flex: 1;
  height: 6px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  overflow: hidden;
  max-width: 150px;
}

.progress-fill-mini {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #10b981);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-percent {
  font-size: 0.8rem;
  font-weight: 600;
  color: #3b82f6;
}

/* Tab badge */
.tab-badge {
  background: var(--color-primary);
  color: white;
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 6px;
}

/* Curriculum Section */
.curriculum-section {
  padding: var(--spacing-md);
}

.curriculum-header {
  margin-bottom: var(--spacing-lg);
}

.curriculum-header h2 {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin: 0 0 var(--spacing-sm) 0;
}

.curriculum-stats {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
}

.curriculum-stats span {
  margin-right: var(--spacing-sm);
}

/* Video List in Curriculum */
.video-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.video-item {
  display: flex;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: #f8f9fa;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s ease;
  align-items: flex-start;
  border: 2px solid transparent;
}

.video-item:hover:not(.locked) {
  background: #e9ecef;
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.video-item.completed {
  background: rgba(16, 185, 129, 0.08);
  border-color: rgba(16, 185, 129, 0.3);
}

.video-item.in-progress {
  background: rgba(59, 130, 246, 0.08);
  border-color: rgba(59, 130, 246, 0.3);
}

.video-item.locked {
  cursor: not-allowed;
  opacity: 0.7;
}

.video-index {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-primary);
  color: white;
  border-radius: 50%;
  font-weight: 700;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.video-item.completed .video-index {
  background: #10b981;
}

.video-item.in-progress .video-index {
  background: #3b82f6;
}

.video-info {
  flex: 1;
  min-width: 0;
}

.video-info h4 {
  margin: 0 0 var(--spacing-xs) 0;
  color: var(--color-text-primary);
  font-size: 1rem;
}

.video-description {
  margin: 0 0 var(--spacing-xs) 0;
  color: var(--color-text-secondary);
  font-size: 0.85rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.video-meta {
  display: flex;
  gap: var(--spacing-md);
  font-size: 0.85rem;
  flex-wrap: wrap;
}

.video-duration {
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  gap: 4px;
}

.completed-badge {
  color: #10b981;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
}

.progress-badge {
  color: #3b82f6;
  font-weight: 500;
}

.video-progress-bar {
  margin-top: var(--spacing-xs);
  height: 4px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.video-progress-bar .progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #10b981);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.video-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  flex-shrink: 0;
}

.video-action i {
  font-size: 1.5rem;
  color: var(--color-primary);
}

.video-action .completed-icon {
  color: #10b981;
}

.video-item.locked .video-action i {
  color: #9ca3af;
}

.course-actions {
  display: flex;
  gap: var(--spacing-md);
}

.course-thumbnail {
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.course-thumbnail img {
  width: 100%;
  height: auto;
  display: block;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius);
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.btn-large {
  padding: var(--spacing-md) var(--spacing-xl);
  font-size: 1.1rem;
}

.purchase-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.free-course-notice {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1.5px solid #86efac;
  border-radius: 10px;
  padding: 0.875rem 1rem;
  margin-bottom: 1rem;
  color: #14532d;
}

.free-course-notice i {
  color: #16a34a;
  font-size: 1.125rem;
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.free-course-notice div {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.free-course-notice strong {
  font-size: 0.875rem;
}

.free-course-notice span {
  font-size: 0.8rem;
  color: #166534;
}

.btn-free {
  background: #16a34a;
  color: white;
}

.btn-free:hover:not(:disabled) {
  background: #15803d;
}

.member-discount-note {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.member-discount-note i {
  color: #fbbf24;
}

.member-discount-note.verified {
  color: #10b981;
  background: #d1fae5;
  padding: 0.5rem 1rem;
  border-radius: 8px;
}

.member-discount-note.verified i {
  color: #10b981;
}

.member-discount-note .original-price {
  text-decoration: line-through;
  color: #9ca3af;
  margin-right: 0.25rem;
}

.enrolled-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.completion-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background: #f0fdf4;
  border: 2px solid #10b981;
  border-radius: 12px;
}

.completion-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.2rem;
  font-weight: 700;
  color: #10b981;
}

.completion-badge i {
  font-size: 1.5rem;
}

.completion-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.btn-certificate {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-certificate:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
}

.btn-certificate:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ce-credits-note {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  color: #059669;
  font-weight: 600;
  margin: 0;
}

.ce-credits-note i {
  color: #fbbf24;
  font-size: 1.1rem;
}

/* Quiz Locked Notice */
.quiz-locked-notice {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md) var(--spacing-lg);
  background: linear-gradient(135deg, rgba(156, 163, 175, 0.1) 0%, rgba(107, 114, 128, 0.1) 100%);
  border: 2px solid rgba(156, 163, 175, 0.3);
  border-radius: var(--border-radius);
  color: #6b7280;
}

.quiz-locked-notice i {
  font-size: 1.5rem;
  color: #9ca3af;
}

.quiz-locked-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.quiz-locked-title {
  font-weight: 600;
  font-size: 1rem;
  color: #4b5563;
}

.quiz-locked-desc {
  font-size: 0.85rem;
  color: #6b7280;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-secondary);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-success {
  background: #4caf50;
  color: white;
}

.btn-success:hover {
  background: #45a049;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.btn-secondary {
  background: white;
  color: var(--color-primary);
  border: 2px solid var(--color-primary);
}

.btn-secondary:hover {
  background: var(--color-primary);
  color: white;
}

/* Tabs */
.course-tabs {
  background: white;
  border-radius: var(--border-radius);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.tab-buttons {
  display: flex;
  border-bottom: 2px solid var(--color-border);
  background: #f8f9fa;
}

.tab-button {
  flex: 1;
  padding: var(--spacing-md) var(--spacing-lg);
  background: none;
  border: none;
  font-weight: 600;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
}

.tab-button:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--color-primary);
}

.tab-button.active {
  color: var(--color-primary);
  background: white;
  border-bottom: 3px solid var(--color-primary);
}

.tab-content {
  padding: var(--spacing-xl);
}

.tab-pane h2 {
  font-size: 1.5rem;
  margin-bottom: var(--spacing-lg);
  color: var(--color-text-primary);
}

.tab-pane h3 {
  font-size: 1.2rem;
  margin: var(--spacing-xl) 0 var(--spacing-md) 0;
  color: var(--color-text-primary);
}

/* Overview Section */
.course-description {
  line-height: 1.8;
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-xl);
}

/* Learning Objectives in Header */
.learning-objectives-header ul {
  list-style: none;
  padding: 0;
  margin: var(--spacing-md) 0;
}

.learning-objectives-header li {
  padding: var(--spacing-sm) 0;
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  list-style-type: none;
}

.learning-objectives-header li::before {
  content: none;
}

.learning-objectives-header li i {
  color: #4caf50;
  margin-top: 2px;
  flex-shrink: 0;
}

/* Learning Objectives in Overview Tab */
.learning-objectives ul,
.prerequisites,
.target-audience {
  margin-bottom: var(--spacing-xl);
}

.learning-objectives ul {
  list-style: none;
  padding: 0;
}

.learning-objectives li {
  padding: var(--spacing-sm) 0;
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  list-style-type: none;
}

.learning-objectives li::before {
  content: none;
}

.learning-objectives li i {
  color: #4caf50;
  margin-top: 4px;
  flex-shrink: 0;
}

/* Video List */
.video-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.video-item {
  display: flex;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: #f8f9fa;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s ease;
  align-items: center;
}

.video-item:hover {
  background: #e9ecef;
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.video-index {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-primary);
  color: white;
  border-radius: 50%;
  font-weight: 700;
  flex-shrink: 0;
}

.video-info {
  flex: 1;
}

.video-info h4 {
  margin: 0 0 var(--spacing-xs) 0;
  color: var(--color-text-primary);
}

.video-info p {
  margin: 0 0 var(--spacing-xs) 0;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

.video-meta {
  display: flex;
  gap: var(--spacing-md);
  font-size: 0.85rem;
}

.video-duration {
  color: var(--color-text-secondary);
}

.completed-badge {
  color: #4caf50;
  font-weight: 600;
}

.video-action {
  color: var(--color-primary);
  font-size: 2rem;
}

/* Reviews */
.reviews-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.review-item {
  padding: var(--spacing-lg);
  background: #f8f9fa;
  border-radius: var(--border-radius);
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-md);
}

.reviewer-info {
  display: flex;
  gap: var(--spacing-md);
  align-items: center;
}

.reviewer-avatar {
  font-size: 2.5rem;
  color: var(--color-primary);
}

.reviewer-info h4 {
  margin: 0 0 var(--spacing-xs) 0;
}

.review-rating i {
  color: #ffc107;
  margin-right: 2px;
}

.review-date {
  font-size: 0.85rem;
  color: var(--color-text-secondary);
}

.review-text {
  line-height: 1.6;
  color: var(--color-text-secondary);
}

/* Presenter Section */
.presenter-section {
  padding: var(--spacing-xl);
}

.presenter-section h2 {
  margin: 0 0 var(--spacing-xl) 0;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.instructor-info {
  max-width: 800px;
}

.instructor-header {
  display: flex;
  gap: var(--spacing-lg);
  align-items: flex-start;
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-xl);
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: var(--border-radius);
  border-left: 4px solid var(--color-primary);
}

.instructor-avatar {
  font-size: 4rem;
  color: var(--color-primary);
  line-height: 1;
}

.instructor-details h3 {
  margin: 0 0 var(--spacing-sm) 0;
  font-size: 1.75rem;
  color: var(--color-text-primary);
}

.credentials {
  color: var(--color-primary);
  font-weight: 600;
  margin: 0;
  font-size: 1rem;
}

.instructor-bio {
  margin-bottom: var(--spacing-xl);
}

.instructor-bio h4 {
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-md) 0;
  font-size: 1.25rem;
}

.bio-content {
  line-height: 1.8;
  color: var(--color-text-secondary);
  padding: var(--spacing-lg);
  background: #f9fafb;
  border-radius: var(--border-radius);
  white-space: pre-line;
}

.certification-info {
  padding: var(--spacing-lg);
  background: #dbeafe;
  border-radius: var(--border-radius);
  border-left: 4px solid #3b82f6;
}

.certification-info h4 {
  color: #1e40af;
  margin: 0 0 var(--spacing-sm) 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.certification-info p {
  margin: 0;
  color: #1e3a8a;
  font-weight: 600;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: var(--spacing-xxl);
  color: var(--color-text-secondary);
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: var(--spacing-md);
  opacity: 0.5;
}

/* Responsive */
@media (max-width: 968px) {
  .course-header {
    grid-template-columns: 1fr;
  }

  .course-thumbnail {
    order: -1;
  }
}

@media (max-width: 768px) {
  .tab-buttons {
    flex-direction: column;
  }

  .course-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }

  .course-stats {
    flex-direction: column;
    gap: var(--spacing-sm);
  }
}

/* Research Tab Styles */
.research-section {
  padding: var(--spacing-md) 0;
}

/* Unified Search Section */
.unified-search-section {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: var(--radius-lg);
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
}

.search-header-row {
  margin-bottom: var(--spacing-lg);
}

.search-title h2 {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin: 0 0 var(--spacing-xs) 0;
  color: var(--color-text-primary);
  font-size: 1.5rem;
}

.search-title h2 i {
  color: #0284c7;
}

.search-subtitle {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 0.95rem;
}

.search-bar-wrapper {
  display: flex;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
}

.search-input-container {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.search-input-container .search-icon {
  position: absolute;
  left: var(--spacing-md);
  color: #94a3b8;
  font-size: 1rem;
}

.unified-search-input {
  width: 100%;
  padding: var(--spacing-md) var(--spacing-xl) var(--spacing-md) 2.5rem;
  border: 2px solid #e2e8f0;
  border-radius: var(--radius-md);
  font-size: 1rem;
  transition: all 0.2s ease;
  background: white;
}

.unified-search-input:focus {
  outline: none;
  border-color: #0284c7;
  box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.1);
}

/* Search Results Tabs */
.search-results-tabs {
  display: flex;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-lg);
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: var(--spacing-sm);
}

.search-results-tabs .tab-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: none;
  border: none;
  color: var(--color-text-secondary);
  font-size: 0.95rem;
  cursor: pointer;
  border-radius: var(--radius-md);
  transition: all 0.2s ease;
}

.search-results-tabs .tab-btn:hover {
  background: rgba(2, 132, 199, 0.1);
  color: #0284c7;
}

.search-results-tabs .tab-btn.active {
  background: #0284c7;
  color: white;
}

.search-results-tabs .clear-btn {
  margin-left: auto;
}

/* Article Results List */
.article-results-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.article-result-card {
  background: white;
  border-radius: var(--radius-md);
  padding: var(--spacing-lg);
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.article-result-card:hover {
  border-color: #0284c7;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.article-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-sm);
}

.article-source {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: 0.8rem;
  color: var(--color-text-secondary);
  padding: var(--spacing-xs) var(--spacing-sm);
  background: #f1f5f9;
  border-radius: var(--radius-sm);
}

.article-title {
  margin: 0 0 var(--spacing-sm) 0;
  font-size: 1.05rem;
  line-height: 1.4;
}

.article-title a {
  color: var(--color-text-primary);
  text-decoration: none;
  transition: color 0.2s ease;
}

.article-title a:hover {
  color: #0284c7;
}

.article-meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-sm);
  font-size: 0.85rem;
  color: var(--color-text-secondary);
}

.article-meta span {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.article-meta .journal {
  font-style: italic;
}

.article-abstract {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  line-height: 1.6;
  margin: var(--spacing-sm) 0;
}

.article-actions {
  display: flex;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-sm);
}

.no-results-hint {
  font-size: 0.85rem;
  margin-top: var(--spacing-sm);
}

.clear-search-btn {
  position: absolute;
  right: var(--spacing-sm);
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--spacing-xs);
  color: #94a3b8;
  transition: color 0.2s ease;
}

.clear-search-btn:hover {
  color: #64748b;
}

.search-submit-btn {
  white-space: nowrap;
  padding: var(--spacing-md) var(--spacing-xl);
}

.search-loading {
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--color-text-secondary);
}

.course-search-results {
  background: white;
  border-radius: var(--radius-md);
  padding: var(--spacing-lg);
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
  padding-bottom: var(--spacing-md);
  border-bottom: 1px solid var(--color-border);
}

.results-header h4 {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin: 0;
  color: var(--color-text-primary);
}

.results-header h4 i {
  color: #0284c7;
}

.course-results-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--spacing-lg);
}

.course-result-card {
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  border-radius: var(--radius-md);
  overflow: hidden;
  transition: all 0.2s ease;
  border: 1px solid var(--color-border);
}

.course-result-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #0284c7;
}

.course-result-thumbnail {
  height: 120px;
  overflow: hidden;
}

.course-result-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.course-result-content {
  padding: var(--spacing-md);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  flex: 1;
}

.course-result-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  line-height: 1.4;
}

.course-result-desc {
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  line-height: 1.5;
}

.course-result-meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  font-size: 0.8rem;
  color: var(--color-text-secondary);
}

.course-result-meta span {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.course-result-meta i {
  color: #0284c7;
}

.course-result-btn {
  margin-top: auto;
  align-self: flex-start;
}

.no-results {
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--color-text-secondary);
  background: white;
  border-radius: var(--radius-md);
}

.no-results i {
  font-size: 2rem;
  margin-bottom: var(--spacing-md);
  opacity: 0.5;
  display: block;
}

.section-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--color-border), transparent);
  margin: var(--spacing-xl) 0;
}

.research-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-xl);
  flex-wrap: wrap;
  gap: var(--spacing-md);
}

.research-title h2 {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-xs);
}

.research-subtitle {
  color: var(--color-text-secondary);
  font-size: 0.95rem;
}

.research-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: var(--spacing-lg);
}

.research-grid.compact {
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: var(--spacing-md);
}

.research-card {
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--spacing-lg);
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.research-card:hover {
  border-color: var(--color-primary);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.research-card.compact {
  padding: var(--spacing-md);
}

.research-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.resource-type {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--radius-sm);
  background: #e3f2fd;
  color: #1565c0;
}

.resource-type.pubmed {
  background: #e8f5e9;
  color: #2e7d32;
}

.resource-type.external {
  background: #fff3e0;
  color: #e65100;
}

.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  padding: var(--spacing-xs);
  transition: all 0.2s ease;
}

.btn-icon:hover {
  color: #f59e0b;
  transform: scale(1.1);
}

.btn-icon.is-favorite {
  color: #f59e0b;
}

.resource-title {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
}

.resource-title a {
  color: var(--color-text-primary);
  text-decoration: none;
  transition: color 0.2s ease;
}

.resource-title a:hover {
  color: var(--color-primary);
}

.resource-meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  font-size: 0.85rem;
  color: var(--color-text-secondary);
}

.resource-meta span {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.resource-meta i {
  font-size: 0.8rem;
  opacity: 0.7;
}

.resource-abstract {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin: var(--spacing-sm) 0;
}

.resource-notes {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  background: #fffbeb;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-sm);
  font-size: 0.85rem;
  color: #92400e;
}

.resource-notes i {
  margin-top: 2px;
}

.resource-actions {
  display: flex;
  gap: var(--spacing-sm);
  margin-top: auto;
  padding-top: var(--spacing-sm);
}

.resource-actions .btn {
  flex: 1;
}

.research-empty {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: var(--radius-lg);
  padding: var(--spacing-xxl);
}

.research-empty h3 {
  margin-bottom: var(--spacing-sm);
  color: var(--color-text-primary);
}

.research-empty p {
  max-width: 500px;
  margin: 0 auto var(--spacing-lg);
}

.org-resources-section {
  margin-top: var(--spacing-xxl);
  padding-top: var(--spacing-xl);
  border-top: 1px solid var(--color-border);
}

.org-resources-section h3 {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-xs);
}

.section-subtitle {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  margin-bottom: var(--spacing-lg);
}

.tab-badge.research-badge {
  background: #10b981;
  color: white;
}

@media (max-width: 768px) {
  .research-grid {
    grid-template-columns: 1fr;
  }
  
  .research-header {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>

